import type { IssueSeverity } from '@/shared/constants/issue-status'

import { useGisIssueView } from '../../../stores/gisIssueViewStore'
import { FilterDropdown, MultiFilterDropdown, OptionRow, type FacetOption } from '../../ui/FilterDropdown'

import type { IssueStatusView } from '../../../lib/issues'

export type { FacetOption }

export interface IssuesFilterOptions {
  status: Record<IssueStatusView, number>
  severity: Record<IssueSeverity, number>
  /** The 6 curated "Issue type" headings (`issue-type-catalog.ts`), flat, with counts — replaces
   *  the raw section_title list. Selecting one also defaults Group by to "Question" (see
   *  `toggleIssueType`), so its specific questions surface in the list's Group-by rail. */
  issueTypeHeadings: FacetOption[]
  entities: FacetOption[]
  departments: FacetOption[]
  hods: FacetOption[]
  mandals: FacetOption[]
}

const SEVERITIES: { key: IssueSeverity; label: string; color: string }[] = [
  { key: 'critical', label: 'Critical', color: '#dc2626' },
  { key: 'non_critical', label: 'Non-critical', color: '#ea580c' },
]

const STATUS_VIEWS: { key: IssueStatusView; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'closed', label: 'Closed' },
]

interface IssuesFilterDropdownsProps {
  options: IssuesFilterOptions
  /** Page-level mandal filter (URL-backed) — read + toggled here like any other facet. */
  activeMandals: readonly string[]
  onToggleMandal: (name: string) => void
}

/**
 * The Issues toolbar's filter strip: one INDIVIDUAL dropdown per dimension — Status · Importance ·
 * Issue type · Entity · Department · HOD · Mandal — sitting left-aligned beside the search box.
 * Facet state lives in `gisIssueViewStore` (mandal is the URL-backed page filter), so the map
 * rings, the chips row and the list react in lockstep.
 */
export function IssuesFilterDropdowns({ options, activeMandals, onToggleMandal }: IssuesFilterDropdownsProps) {
  const v = useGisIssueView
  const statusView = v((s) => s.statusView)
  const severity = v((s) => s.severity)
  const issueType = v((s) => s.issueType)
  const entity = v((s) => s.entity)
  const department = v((s) => s.department)
  const hod = v((s) => s.hod)
  const setStatusView = v((s) => s.setStatusView)
  const toggleSeverity = v((s) => s.toggleSeverity)
  const toggleIssueType = v((s) => s.toggleIssueType)
  const toggleEntity = v((s) => s.toggleEntity)
  const toggleDepartment = v((s) => s.toggleDepartment)
  const toggleHod = v((s) => s.toggleHod)

  const statusLabel = STATUS_VIEWS.find((s) => s.key === statusView)?.label ?? 'Open'

  return (
    <>
      <FilterDropdown label="Status" valueText={statusLabel}>
        {STATUS_VIEWS.map((s) => (
          <OptionRow
            key={s.key}
            radio
            checked={statusView === s.key}
            label={s.label}
            count={options.status[s.key]}
            onToggle={() => setStatusView(s.key)}
          />
        ))}
      </FilterDropdown>
      <FilterDropdown label="Importance" activeCount={severity.length}>
        {SEVERITIES.map((s) => (
          <OptionRow
            key={s.key}
            checked={severity.includes(s.key)}
            label={s.label}
            count={options.severity[s.key]}
            swatch={s.color}
            onToggle={() => toggleSeverity(s.key)}
          />
        ))}
      </FilterDropdown>
      <MultiFilterDropdown label="Issue type" options={options.issueTypeHeadings} active={issueType} onToggle={toggleIssueType} />
      <MultiFilterDropdown label="Entity" options={options.entities} active={entity} onToggle={toggleEntity} />
      <MultiFilterDropdown label="Department" options={options.departments} active={department} onToggle={toggleDepartment} />
      <MultiFilterDropdown label="HOD" options={options.hods} active={hod} onToggle={toggleHod} />
      <MultiFilterDropdown label="Mandal" options={options.mandals} active={activeMandals} onToggle={onToggleMandal} />
    </>
  )
}
